/**
 * AIストラテジー - Genetic（遺伝的アルゴリズム最適化版）
 *
 * hard.ts をベースに、固定値をゲノムパラメータで置き換えたストラテジー。
 * GeneticTrainer によって最適化されたパラメータを使用して意思決定を行う。
 */

import { shouldSkipAttackOnMonster } from './base';
import type { AIStrategy, AIGameState, UsableSkill, ChargeableMonster, ChainContext } from './base';
import { normalStrategy } from './normal';
import { isPhaseCardChargeRecommended } from '../phaseCardChargeChecker';
import type { CardWithId, MonsterInstance, HandCard } from '../../../types/card';
import type { DeepSeaCard } from '../../deepSeaZone/types';
import {
  hasHikou,
  isGroundMonster,
  getCounterAttackMultiplier,
  getDirectAttackStatus,
} from '../../keywordAbilities';
import type { AIGenome } from '../genetic/genome';
import { DEFAULT_GENOME } from '../genetic/genome';
import { convertToGameState } from './simulation';
import { chooseBestMagicCard } from '../evaluation';

// =============================================================================
// 刹那詠唱カードID定義（効果タイプ別）- hard.ts と共通
// =============================================================================

const ATTACK_RESPONSE_SETSUNA_IDS = new Set([
  'C0000013', 'C0000075', 'C0000176', 'C0000238', 'C0000243', 'C0000389', 'C0000402',
  'C0000087', 'C0000150', 'C0000226', 'C0000288',
  'C0000048', 'C0000178',
  'C0000153', 'C0000237',
  'C0000391',
]);

const DAMAGE_SETSUNA_IDS = new Set([
  'C0000022', 'C0000032', 'C0000065', 'C0000133', 'C0000136', 'C0000194', 'C0000254',
  'C0000323', 'C0000349', 'C0000388', 'C0000390', 'C0000429',
  'C0000015', 'C0000064', 'C0000209',
]);

const BATTLE_START_ONLY_SETSUNA_IDS = new Set([
  'C0000050', 'C0000277', 'C0000210', 'C0000217', 'C0000431',
]);

// =============================================================================
// ファクトリー関数
// =============================================================================

/**
 * 遺伝的アルゴリズムで最適化されたゲノムを使用するストラテジーを作成
 *
 * @param genome - 最適化されたゲノムパラメータ（省略時はデフォルト）
 * @returns AIストラテジー
 */
export function createGeneticStrategy(genome: AIGenome = DEFAULT_GENOME): AIStrategy {
  return {
    ...normalStrategy,

    // =========================================================================
    // 召喚判断（ゲノムパラメータ使用）
    // =========================================================================
    chooseSummon(summonableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (summonableCards.length === 0) return null;

      const myMonsterCount = gameState.myField.filter(m => m).length;
      const opponentMonsterCount = gameState.opponentField.filter(m => m).length;

      // 不利時の積極性パラメータで判断
      if (myMonsterCount < opponentMonsterCount) {
        // disadvantageAggression が高いほど攻撃力重視
        if (Math.random() < genome.summonPreference.disadvantageAggression) {
          const sorted = [...summonableCards].sort((a, b) => {
            const aScore = (a.attack || 0) * genome.summonPreference.rawAttackWeight;
            const bScore = (b.attack || 0) * genome.summonPreference.rawAttackWeight;
            return bScore - aScore;
          });
          return sorted[0];
        }
      }

      // コスト効率で判断（costEfficiencyWeight を考慮）
      const sorted = [...summonableCards].sort((a, b) => {
        const aCostEfficiency = (a.attack || 0) / Math.max(1, a.cost || 1);
        const bCostEfficiency = (b.attack || 0) / Math.max(1, b.cost || 1);
        const aRawAttack = (a.attack || 0) * genome.summonPreference.rawAttackWeight;
        const bRawAttack = (b.attack || 0) * genome.summonPreference.rawAttackWeight;

        const aScore = aCostEfficiency * genome.summonPreference.costEfficiencyWeight + aRawAttack;
        const bScore = bCostEfficiency * genome.summonPreference.costEfficiencyWeight + bRawAttack;
        return bScore - aScore;
      });

      return sorted[0];
    },

    // =========================================================================
    // 攻撃対象選択（ゲノムパラメータ使用）
    // =========================================================================
    chooseAttackTarget(validTargets: (number | 'direct')[], attacker: MonsterInstance, gameState: AIGameState): number | 'direct' {
      if (validTargets.length === 0) return 'direct';

      const monsterTargets = validTargets.filter(t => t !== 'direct');
      const attackPower = attacker.currentAttack || attacker.attack || 0;
      const attackerHp = attacker.currentHp || attacker.hp || 0;
      const attackerHasFlight = hasHikou(attacker);

      if (monsterTargets.length === 0) {
        return 'direct';
      }

      // 各ターゲットの評価（ゲノムパラメータ使用）
      const evaluatedTargets = monsterTargets.map(idx => {
        const target = gameState.opponentField[idx];
        if (!target) return { idx, score: -Infinity, shouldSkip: true };

        const targetHp = target.currentHp || 0;
        const targetAttack = target.currentAttack || 0;
        const canKill = targetHp <= attackPower;

        const counterMultiplier = getCounterAttackMultiplier(attacker, target);
        const counterDamage = Math.floor(targetAttack * counterMultiplier);
        const noCounter = counterMultiplier === 0;
        const willDieFromCounter = counterDamage >= attackerHp;

        const shouldSkip = shouldSkipAttackOnMonster(attacker, target, gameState);

        let score = 0;

        if (canKill) {
          // killBonus: 倒せる相手へのボーナス
          score += genome.attackScoring.killBonus;
          // threatWeight: 脅威度の重み
          score += targetAttack * genome.attackScoring.threatWeight;
        } else {
          // damageWeight: 与ダメージの重み
          score += Math.min(attackPower, targetHp) * genome.attackScoring.damageWeight;
          // 脅威度（半分の重み）
          score += targetAttack * genome.attackScoring.threatWeight * 0.5;
        }

        // flightSafeBonus: 飛行安全攻撃ボーナス
        if (noCounter) {
          score += genome.attackScoring.flightSafeBonus;
        }

        // counterDeathPenalty: 反撃死ペナルティ
        if (!noCounter && willDieFromCounter && !canKill) {
          score -= genome.attackScoring.counterDeathPenalty;
        }

        // groundTargetBonus: 地上ターゲット優先
        if (attackerHasFlight && isGroundMonster(target)) {
          score += genome.attackScoring.groundTargetBonus;
        }

        return { idx, score, canKill, targetAttack, shouldSkip, noCounter };
      });

      const validAttackTargets = evaluatedTargets.filter(t => !t.shouldSkip);

      if (validAttackTargets.length === 0) {
        if (validTargets.includes('direct')) {
          const directStatus = getDirectAttackStatus(attacker, gameState.opponentField);
          if (directStatus.canDirect) {
            return 'direct';
          }
        }
        const safest = evaluatedTargets
          .filter(t => t.noCounter)
          .sort((a, b) => b.score - a.score)[0];
        if (safest) return safest.idx;
        return 'direct';
      }

      validAttackTargets.sort((a, b) => b.score - a.score);

      const bestTarget = validAttackTargets[0];
      if (bestTarget && bestTarget.score > 0) {
        if (validTargets.includes('direct')) {
          const directStatus = getDirectAttackStatus(attacker, gameState.opponentField);
          if (directStatus.canDirect) {
            const directDamage = Math.floor(attackPower * directStatus.damageMultiplier);
            if (directDamage >= gameState.opponentLife) {
              return 'direct';
            }
            // directAttackThreshold: 直接攻撃選択閾値
            if (attackerHasFlight && directStatus.counterMultiplier === 0) {
              if (!bestTarget.canKill && directDamage >= bestTarget.score * genome.attackScoring.directAttackThreshold) {
                return 'direct';
              }
            }
          }
        }
        return bestTarget.idx;
      }

      return 'direct';
    },

    // =========================================================================
    // スキル選択（ゲノムパラメータ使用）
    // =========================================================================
    chooseSkill(usableSkills: UsableSkill[], gameState: AIGameState): UsableSkill | null {
      if (usableSkills.length === 0) return null;

      // 上級技があれば advancedSkillPriority の確率で使用
      const advancedSkills = usableSkills.filter(s => s.skillType === 'advanced');
      if (advancedSkills.length > 0) {
        if (Math.random() < genome.skillUsage.advancedSkillPriority) {
          return advancedSkills[0];
        }
      }

      // basicSkillRate: 基本技使用率
      if (Math.random() < genome.skillUsage.basicSkillRate) {
        return usableSkills[0];
      }

      return null;
    },

    // =========================================================================
    // チャージ判断（ゲノムパラメータ使用）
    // =========================================================================
    chooseCharge(chargeableCards: CardWithId[], chargeableMonsters: ChargeableMonster[], gameState: AIGameState): { card: CardWithId; monsterIndex: number } | null {
      if (chargeableCards.length === 0 || chargeableMonsters.length === 0) return null;

      const noChargeMonsters = chargeableMonsters.filter(m => m.currentCharges === 0);
      const oneChargeMonsters = chargeableMonsters.filter(m => m.currentCharges === 1);

      let targetMonster = null;

      // prioritizeAdvancedReady: 上級技準備優先
      if (Math.random() < genome.chargeStrategy.prioritizeAdvancedReady) {
        const oneChargeWithAdvanced = oneChargeMonsters.filter(m => m.monster.advancedSkill);
        if (oneChargeWithAdvanced.length > 0) {
          oneChargeWithAdvanced.sort((a, b) =>
            (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
          );
          targetMonster = oneChargeWithAdvanced[0];
        }
      }

      if (!targetMonster && noChargeMonsters.length > 0) {
        const withBasic = noChargeMonsters.filter(m => m.monster.basicSkill);
        if (withBasic.length > 0) {
          withBasic.sort((a, b) =>
            (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
          );
          targetMonster = withBasic[0];
        } else {
          noChargeMonsters.sort((a, b) =>
            (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
          );
          targetMonster = noChargeMonsters[0];
        }
      }

      if (!targetMonster && oneChargeMonsters.length > 0) {
        oneChargeMonsters.sort((a, b) =>
          (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
        );
        targetMonster = oneChargeMonsters[0];
      }

      if (!targetMonster) return null;

      const targetAttribute = targetMonster.monster.attribute;
      const sameAttributeCards = chargeableCards.filter(card => card.attribute === targetAttribute);

      if (sameAttributeCards.length === 0) return null;

      // preserveHighCostMonsters: 高コストモンスター温存
      const sortedCards = [...sameAttributeCards].sort((a, b) => {
        if (a.type !== 'monster' && b.type === 'monster') return -1;
        if (a.type === 'monster' && b.type !== 'monster') return 1;

        // 高コストモンスターを温存
        if (a.type === 'monster' && b.type === 'monster') {
          const aCost = a.cost || 0;
          const bCost = b.cost || 0;
          // preserveHighCostMonsters が高いほど、高コストを温存
          if (aCost >= 4 && Math.random() < genome.chargeStrategy.preserveHighCostMonsters) {
            return 1; // a を後ろに
          }
          if (bCost >= 4 && Math.random() < genome.chargeStrategy.preserveHighCostMonsters) {
            return -1; // b を後ろに
          }
        }

        return (a.cost || 0) - (b.cost || 0);
      });

      return {
        card: sortedCards[0],
        monsterIndex: targetMonster.monsterIndex,
      };
    },

    // =========================================================================
    // トリガー発動（ゲノムパラメータ使用）
    // =========================================================================
    chooseTrigger(activatableTriggers: unknown[], gameState: AIGameState): unknown | null {
      if (activatableTriggers.length === 0) return null;

      // triggerActivationRate: トリガー発動率
      if (Math.random() < genome.actionProbabilities.triggerActivationRate) {
        return activatableTriggers[0];
      }
      return null;
    },

    // =========================================================================
    // 刹那詠唱（hard.ts と同じロジック）
    // =========================================================================
    chooseSetsuna(availableSetsunaCards: CardWithId[], chainContext: ChainContext, gameState: AIGameState): CardWithId | null {
      if (availableSetsunaCards.length === 0) return null;

      const attackResponseCard = availableSetsunaCards.find(c =>
        ATTACK_RESPONSE_SETSUNA_IDS.has(c.id)
      );
      const damageCard = availableSetsunaCards.find(c =>
        DAMAGE_SETSUNA_IDS.has(c.id)
      );
      const battleStartCard = availableSetsunaCards.find(c =>
        BATTLE_START_ONLY_SETSUNA_IDS.has(c.id)
      );

      if (chainContext.attacker) {
        if (attackResponseCard) return attackResponseCard;
        if (damageCard) return damageCard;
      }

      if (chainContext.type === 'battleStart') {
        const hasCombatCard = attackResponseCard || damageCard;

        if (!hasCombatCard && battleStartCard) {
          return battleStartCard;
        }

        if (damageCard) {
          return damageCard;
        }
      }

      return null;
    },

    // =========================================================================
    // 魔法カード使用（シミュレーション評価 + ゲノムパラメータ）
    // =========================================================================
    chooseMagicCard(usableMagicCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (usableMagicCards.length === 0) return null;

      try {
        // AIGameStateをGameStateに変換
        const state = convertToGameState(gameState);

        // シミュレーション評価で最適カードを選択
        // genome.boardEvaluation: 盤面評価パラメータ
        // genome.magicEvaluation: 魔法評価パラメータ（SPコストペナルティ、閾値）
        const bestCard = chooseBestMagicCard(
          usableMagicCards,
          state,
          gameState.currentPlayer,
          genome.boardEvaluation,
          genome.magicEvaluation
        );

        return bestCard;
      } catch (error) {
        // エラー時は使用しない
        console.warn('GeneticStrategy magic evaluation error:', error);
        return null;
      }
    },

    // =========================================================================
    // フィールドカード配置（ゲノムパラメータ使用）
    // =========================================================================
    chooseFieldCard(placeableFieldCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (placeableFieldCards.length === 0) return null;

      const myMonsters = gameState.myField.filter(m => m).length;
      const opponentMonsters = gameState.opponentField.filter(m => m).length;
      const turn = gameState.turn || 1;

      if (opponentMonsters >= 2 && myMonsters === 0) return null;

      const scored = placeableFieldCards.map(fieldCard => {
        let score = 0;
        const effect = fieldCard.effect || '';

        // earlyGameBonus: 序盤配置ボーナス
        if (turn <= 2) score += genome.cardPlacement.earlyGameBonus;
        else if (turn <= 4) score += genome.cardPlacement.earlyGameBonus * 0.5;

        // コスト軽減効果
        if (effect.includes('コスト') && (effect.includes('軽減') || effect.includes('-'))) {
          score += 50;
        }

        // synergyHandWeight: 手札シナジー重み
        const synergisticMonstersInHand = gameState.myHand.filter(card => {
          if (card.type !== 'monster') return false;
          if (card.attribute === fieldCard.attribute) return true;
          if (effect.includes('《') && card.category) {
            const categoryMatch = effect.match(/《([^》]+)》/);
            if (categoryMatch && card.category.includes(categoryMatch[1])) return true;
          }
          return false;
        });
        score += synergisticMonstersInHand.length * genome.cardPlacement.synergyHandWeight;

        // synergyFieldWeight: フィールドシナジー重み
        const synergisticMonstersOnField = gameState.myField.filter(monster => {
          if (!monster) return false;
          if (monster.attribute === fieldCard.attribute) return true;
          if (effect.includes('《') && monster.category) {
            const categoryMatch = effect.match(/《([^》]+)》/);
            if (categoryMatch && monster.category.includes(categoryMatch[1])) return true;
          }
          return false;
        });
        score += synergisticMonstersOnField.length * genome.cardPlacement.synergyFieldWeight;

        // エンドフェイズダメージ効果
        if (effect.includes('エンドフェイズ') && effect.includes('ダメージ')) {
          score += opponentMonsters * 10;
          score += 10;
        }

        // SP回復効果
        if (effect.includes('SP') && (effect.includes('回復') || effect.includes('アクティブ'))) {
          score += 20;
        }

        // 攻撃力アップ効果
        if (effect.includes('攻撃力') && effect.includes('アップ')) {
          score += (synergisticMonstersOnField.length + synergisticMonstersInHand.length) * 10;
        }

        return { card: fieldCard, score };
      });

      scored.sort((a, b) => b.score - a.score);

      // fieldCardThreshold: 配置スコア閾値
      if (scored[0] && scored[0].score >= genome.cardPlacement.fieldCardThreshold) {
        return scored[0].card;
      }

      const availableSP = gameState.myActiveSP + (gameState.myBonusSP || 0);
      const hasSummonableMonster = gameState.myHand.some(card =>
        card.type === 'monster' && card.cost <= availableSP
      );
      const hasUsableMagic = gameState.myHand.some(card =>
        card.type === 'magic' && card.cost <= availableSP
      );
      const hasEmptySlot = gameState.myField.some(slot => !slot);

      if (!hasSummonableMonster && !hasUsableMagic && scored[0]) {
        return scored[0].card;
      }
      if (hasSummonableMonster && !hasEmptySlot && scored[0]) {
        return scored[0].card;
      }

      return null;
    },

    // =========================================================================
    // フェイズカード配置（ゲノムパラメータ使用）
    // =========================================================================
    choosePhaseCard(placeablePhaseCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (placeablePhaseCards.length === 0) return null;

      const myMonsters = gameState.myField.filter(m => m).length;
      const opponentMonsters = gameState.opponentField.filter(m => m).length;
      const turn = gameState.turn || 1;

      if (opponentMonsters >= 2 && myMonsters === 0) return null;

      const scored = placeablePhaseCards.map(phaseCard => {
        let score = 0;
        const effect = phaseCard.effect || '';
        const isNoneAttribute = phaseCard.attribute === 'なし';

        // earlyGameBonus: 序盤配置ボーナス（フェイズカードは少し高め）
        if (turn <= 2) score += genome.cardPlacement.earlyGameBonus * 1.15;
        else if (turn <= 4) score += genome.cardPlacement.earlyGameBonus * 0.65;

        // コスト軽減効果
        if (effect.includes('コスト') && (effect.includes('軽減') || effect.includes('-'))) {
          score += 50;
        }

        // 手札シナジー
        const synergisticCardsInHand = gameState.myHand.filter(card => {
          if (card.uniqueId === phaseCard.uniqueId) return false;
          if (isNoneAttribute) return true;
          if (card.attribute === phaseCard.attribute) return true;
          if (effect.includes('《') && card.category) {
            const categoryMatch = effect.match(/《([^》]+)》/);
            if (categoryMatch && card.category.includes(categoryMatch[1])) return true;
          }
          return false;
        });
        score += synergisticCardsInHand.length * genome.cardPlacement.synergyHandWeight * 0.75;

        // フィールドシナジー
        const synergisticMonstersOnField = gameState.myField.filter(monster => {
          if (!monster) return false;
          if (isNoneAttribute) return true;
          if (monster.attribute === phaseCard.attribute) return true;
          return false;
        });
        score += synergisticMonstersOnField.length * genome.cardPlacement.synergyFieldWeight * 0.8;

        // チャージ用カード
        const chargeableCards = gameState.myHand.filter(card => {
          if (card.uniqueId === phaseCard.uniqueId) return false;
          if (isNoneAttribute) return true;
          return card.attribute === phaseCard.attribute || card.attribute === 'なし';
        });
        score += chargeableCards.length * 15;

        return { card: phaseCard, score, chargeableCount: chargeableCards.length };
      });

      scored.sort((a, b) => b.score - a.score);

      // phaseCardThreshold: 配置スコア閾値
      if (scored[0] && scored[0].score >= genome.cardPlacement.phaseCardThreshold) {
        return scored[0].card;
      }

      if (scored[0] && scored[0].chargeableCount >= 3) {
        return scored[0].card;
      }

      return null;
    },

    // =========================================================================
    // フェイズカードチャージ（hard.ts と同じロジック）
    // =========================================================================
    choosePhaseCardCharge(phaseCardChargeableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (phaseCardChargeableCards.length === 0) return null;

      const phaseCard = gameState.myPhaseCard;
      const currentStage = phaseCard?.stage !== undefined ? phaseCard.stage : (phaseCard?.charges?.length || 0);

      if (currentStage === 2) {
        if (!isPhaseCardChargeRecommended(phaseCard, gameState)) {
          return null;
        }
      }

      const sorted = [...phaseCardChargeableCards].sort((a, b) => {
        const aIsMonster = a.type === 'monster' ? 1 : 0;
        const bIsMonster = b.type === 'monster' ? 1 : 0;
        if (aIsMonster !== bIsMonster) return aIsMonster - bIsMonster;
        return (a.cost || 0) - (b.cost || 0);
      });

      if (currentStage === 2) {
        return sorted[0];
      }

      const nonMonsterCard = sorted.find(c => c.type !== 'monster');
      if (nonMonsterCard) {
        return nonMonsterCard;
      }

      const cheapMonster = sorted.find(c => c.type === 'monster' && (c.cost || 0) <= 2);
      if (cheapMonster) {
        return cheapMonster;
      }

      if (gameState.myHand.length >= 4) {
        return sorted[0];
      }

      return null;
    },

    // =========================================================================
    // 深海領域アクション（genome.deepSeaStrategy パラメータ使用）
    // =========================================================================
    chooseSubmerge(submergibleCards: HandCard[], gameState: AIGameState): HandCard | null {
      if (submergibleCards.length === 0) return null;

      const { submergeMinSPDeficit, submergeWhenFieldFull } = genome.deepSeaStrategy;
      const emptySlots = gameState.myField.filter(m => m === null).length;
      const totalAvailableSP = gameState.myActiveSP + (gameState.myBonusSP || 0);

      // フィールドに空きがあり、召喚可能なカードがあれば潜航しない
      if (emptySlots > 0) {
        const summonableNow = submergibleCards.filter(c =>
          c.type === 'monster' && (c.cost || 0) <= totalAvailableSP
        );
        if (summonableNow.length > 0) return null;
      }

      // SP不足量が閾値以上のカードを潜航候補に
      const highCostCards = submergibleCards.filter(c => {
        const cost = c.cost || 0;
        const spDeficit = cost - totalAvailableSP;
        return c.type === 'monster' && spDeficit >= submergeMinSPDeficit;
      });

      if (highCostCards.length > 0) {
        // コストが高いものから潜航（深度ボーナスで強化される）
        const sorted = [...highCostCards].sort((a, b) => (b.cost || 0) - (a.cost || 0));
        return sorted[0];
      }

      // フィールドが満杯の場合、確率で潜航
      if (emptySlots === 0 && Math.random() < submergeWhenFieldFull) {
        // 攻撃力が高いものを潜航（深度ボーナスで更に強化）
        const sorted = [...submergibleCards].sort((a, b) => (b.attack || 0) - (a.attack || 0));
        return sorted[0];
      }

      return null;
    },

    chooseSurface(surfaceableCards: DeepSeaCard[], gameState: AIGameState): { deepSeaCard: DeepSeaCard; fieldSlot: number } | null {
      if (surfaceableCards.length === 0) return null;

      const { surfaceMinDepth, surfaceEffectDepth, surfaceWhenEmptyField } = genome.deepSeaStrategy;
      const emptySlots = gameState.myField
        .map((m, i) => m === null ? i : -1)
        .filter(i => i >= 0);

      if (emptySlots.length === 0) return null;

      // 効率計算: (ATK + 深度ボーナス) / 浮上コスト
      const DEPTH_ATK_BONUS = 200;
      const calcEfficiency = (c: DeepSeaCard): number => {
        const surfaceCost = Math.max(1, (c.card.cost || 0) - c.depth);
        const atkWithBonus = (c.card.attack || 0) + (c.depth * DEPTH_ATK_BONUS);
        return atkWithBonus / surfaceCost;
      };

      // surfaceMinDepth以上のカードを浮上検討
      const deepCards = surfaceableCards.filter(c => c.depth >= surfaceMinDepth);

      if (deepCards.length > 0) {
        // surfaceEffectDepth以上は浮上効果発動、その中で最も効率が高いものを選択
        const surfaceEffectCards = deepCards.filter(c => c.depth >= surfaceEffectDepth);
        if (surfaceEffectCards.length > 0) {
          const sorted = [...surfaceEffectCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
          return {
            deepSeaCard: sorted[0],
            fieldSlot: emptySlots[0],
          };
        }

        // 深度3以上で最も効率が高いものを選択
        const sorted = [...deepCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
        return {
          deepSeaCard: sorted[0],
          fieldSlot: emptySlots[0],
        };
      }

      // フィールドにモンスターがいない場合、surfaceWhenEmptyField以上で浮上
      const hasMonster = gameState.myField.some(m => m !== null);
      if (!hasMonster) {
        const emergencyCards = surfaceableCards.filter(c => c.depth >= surfaceWhenEmptyField);
        if (emergencyCards.length > 0) {
          const sorted = [...emergencyCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
          return {
            deepSeaCard: sorted[0],
            fieldSlot: emptySlots[0],
          };
        }
      }

      return null;
    },
  };
}

// デフォルトゲノムを使用するストラテジー（export用）
export const geneticStrategy = createGeneticStrategy();
